import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"

import { useLazyGetTableColumnsQuery } from "store/api/dashboardApi"
import { useLazyGetDevicesQuery } from "store/api/devicesApi"
import { useLazyGetPerformanceTestReportQuery } from "store/api/performanceTestApi"
import { FeatureFlag, isFeatureEnabled } from "store/featureFlagsSlice"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { showPerformanceTestReportModal } from "store/modalSlise"
import { setPerformanceTestReport, setSelectedDevicesForPerformanceTest } from "store/tempDataSlise"
import { ApiTypes, UITypes } from "types"
import PerformanceTestStatus from "types/enums/PerformanceTestStatus"
import { MonitoringReportAnnotations } from "types/MonitoringReportAnnotations"
import { ReactComponent as IconPower } from "assets/svg/icon-power.svg"
import { ReactComponent as IconStatsReport } from "assets/svg/icon-stats-report.svg"
import { ReactComponent as IconPerformanceTest } from "assets/svg/menu-performance.svg"

import { convertDataToMonitoringTableRows } from "utils/convertData"
import decodeCustomUrlParam from "utils/decodeCustomUrlParam"
import { isMobileDevice } from "utils/deviceDetection"
import { reorderTableHeaderCells } from "utils/reorderTabbleColumns"
import useUrlQueryParams from "hooks/useUrlQueryParams"

import { getMonitoringTableHeaderCells } from "./MonitoringTableHeaderCells"

interface UseMonitoringPageContentProps {
  devices: ApiTypes.Model.Device[]
  onPerformanceTestSuccess: () => void
}

export const useMonitoringPageContent = ({ devices, onPerformanceTestSuccess }: UseMonitoringPageContentProps) => {
  const dispatch = useAppDispatch()
  const showAlertRules = useAppSelector(isFeatureEnabled(FeatureFlag.AlertRules))
  const [showPerformanceTestActionsModal, setShowPerformanceTestActionsModal] = useState(false)
  const [showRebootModalDevice, setShowRebootModalDevice] = useState<ApiTypes.Model.Device | undefined>()
  const [selectedDeviceForStatsReport, setSelectedDeviceForStatsReport] = useState<ApiTypes.Model.Device | undefined>()
  const [monitoringReportAnnotations, setMonitoringReportAnnotations] = useState<MonitoringReportAnnotations | null>(
    null,
  )
  const { originalQueryParams, setUrlQueryParams } = useUrlQueryParams()
  const [triggerGetTableColumns, { data: columnsData }] = useLazyGetTableColumnsQuery()
  const [triggerGetDevice] = useLazyGetDevicesQuery()
  const [triggerGetPerformanceTestReport] = useLazyGetPerformanceTestReportQuery()

  useEffect(() => {
    if (originalQueryParams["custom_param"]) {
      const decodedParam = decodeCustomUrlParam(originalQueryParams["custom_param"])

      if (decodedParam) {
        setMonitoringReportAnnotations(decodedParam)
        setUrlQueryParams({ custom_param: null, sn: decodedParam.sn })
      }
    }
  }, [originalQueryParams, setUrlQueryParams])

  const [defaultPeriod, setDefaultPeriod] = useState<string | undefined>()
  const [defaultTab, setDefaultTab] = useState<"liveData" | "reporting" | "alertRules" | undefined>()

  useEffect(() => {
    const openOffline = originalQueryParams["openOffline"]
    const sn = originalQueryParams["sn"]

    if (openOffline && sn && devices.length > 0) {
      const device = devices.find(item => item.sn === sn)

      if (device) {
        setDefaultPeriod("last30Days")
        setDefaultTab("reporting")
        setSelectedDeviceForStatsReport(device)
        setUrlQueryParams({ openOffline: null })
      }
    }
  }, [devices, originalQueryParams, setUrlQueryParams])

  useEffect(() => {
    if (monitoringReportAnnotations && devices.length > 0) {
      const device = devices.find(item => item.sn === monitoringReportAnnotations.sn)

      if (device) {
        setSelectedDeviceForStatsReport(device)
      }
    }
  }, [devices, monitoringReportAnnotations])

  const openPerformanceTestModal = async (testId: number, deviceSn: string) => {
    try {
      const deviceData = await triggerGetDevice({ sn: deviceSn })

      if (deviceData.error) {
        toast.error("Something went wrong, please try again")
        return
      }

      const device = deviceData.data?.data[0]
      const res = await triggerGetPerformanceTestReport(testId)

      if (res.data && device) {
        dispatch(
          setPerformanceTestReport({
            ...res?.data,
            customer: device.homeowner_name,
            zip_code: device.zip?.code,
            where_installed_name: device.residence_type?.name,
            timeZone: device.time_zone_text || "",
          }),
        )
        dispatch(showPerformanceTestReportModal(true))
      }
    } catch {
      toast.error("Failed to load performance test report")
    }
  }

  useEffect(() => {
    const testId = originalQueryParams?.testId
    const sn = originalQueryParams?.sn

    if (testId && sn) {
      const testIdNumber = Number(testId)
      openPerformanceTestModal(testIdNumber, sn)
    }
  }, [originalQueryParams?.testId, originalQueryParams?.sn])

  const tableHeaderCells = useMemo((): UITypes.TableHeaderCell[] => {
    const baseHeaderCells = getMonitoringTableHeaderCells(showAlertRules)

    return columnsData?.data?.config
      ? reorderTableHeaderCells(baseHeaderCells, columnsData?.data?.config)
      : baseHeaderCells
  }, [columnsData, showAlertRules])

  useEffect(() => {
    triggerGetTableColumns({ list_name: "contractor_monitoring_list" })
  }, [triggerGetTableColumns])

  const handleShowActionsModal = (id: number) => {
    const device = devices.find(item => item.id === id)

    dispatch(
      setSelectedDevicesForPerformanceTest({
        isImmediate: true,
        devices: [
          ...[{ id, email: device?.email, sn: device?.sn, system_type: device?.system_type }],
        ] as ApiTypes.Model.PerformanceTestDevice[],
      }),
    )
    setShowPerformanceTestActionsModal(true)
  }

  const handleShowStatsReportModal = (id: number) => {
    const device = devices.find(item => item.id === id)

    setSelectedDeviceForStatsReport(device)
  }

  const handleClearMonitoringReportAnnotations = () => {
    setMonitoringReportAnnotations(null)
  }

  const handleCancelStatsReportModal = () => {
    setSelectedDeviceForStatsReport(undefined)
    setDefaultPeriod(undefined)
    setDefaultTab(undefined)
    handleClearMonitoringReportAnnotations()
  }

  const handleShowRebootModal = (id: number) => {
    const device = devices.find(item => item.id === id)

    setShowRebootModalDevice(device)
  }

  const handleCloseRebootModal = () => {
    setShowRebootModalDevice(undefined)
  }

  const handleClosePerformanceTestActionsModal = () => {
    setShowPerformanceTestActionsModal(false)
  }

  const getRowActions = (rowData: UITypes.TableBodyRow) => {
    const isOffline = rowData.rowSettings.item.work_status === "offline"
    const isRunning = rowData.rowSettings.item.perftest?.status === PerformanceTestStatus.RUNNING

    return {
      actionButtons: [
        {
          icon: <IconPower />,
          tooltipPosition: "top" as const,
          tooltip: (
            <>
              Reboot device
              {isOffline && (
                <>
                  <br />
                  (device is offline)
                </>
              )}
            </>
          ),
          disabled: isOffline,
          handler: handleShowRebootModal,
        },
        {
          icon: <IconPerformanceTest />,
          tooltipPosition: "top-start" as const,
          tooltip: (
            <>
              Start performance test
              {isOffline && (
                <>
                  <br />
                  (device is offline)
                </>
              )}
              {isRunning && !isOffline && (
                <>
                  <br />
                  (performance test is running)
                </>
              )}
            </>
          ),
          disabled: isOffline || isRunning,
          handler: handleShowActionsModal,
        },
        {
          icon: <IconStatsReport />,
          tooltipPosition: "top-start" as const,
          tooltip: "Monitoring report",
          handler: handleShowStatsReportModal,
        },
      ],
    }
  }

  return {
    defaultPeriod,
    defaultTab,
    handleClosePerformanceTestActionsModal,
    handleCloseRebootModal,
    getRowActions,
    handleCancelStatsReportModal,
    handleClearMonitoringReportAnnotations,
    handlePerformanceTestSuccess: onPerformanceTestSuccess,
    isFetchingTableColumns: !columnsData,
    isMobileDevice: isMobileDevice(),
    monitoringReportAnnotations,
    selectedDeviceForStatsReport,
    setShowPerformanceTestActionsModal,
    showPerformanceTestActionsModal,
    showRebootModalDevice,
    tableHeaderCells,
    tableData: convertDataToMonitoringTableRows(devices),
  }
}
